import { Link } from "react-router-dom";
import { fmtMoney, fmtNumber, imgUrl } from "@/lib/api";
import { Gauge, Fuel, Cog, MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS_STYLES = {
  available: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  reserved: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  sold: "bg-red-500/15 text-red-400 border-red-500/30",
};

export const VehicleCard = ({ vehicle, index = 0, compareEnabled, compared, onCompare }) => {
  const img = vehicle.images?.[0]?.url;
  const est = vehicle.price ? Math.round((vehicle.price * 0.9 * 0.0239)) : 0; // rough 48mo preview
  return (
    <div
      data-testid={`vehicle-card-${vehicle.id}`}
      className="card-surface hover-lift group relative overflow-hidden"
    >
      {vehicle.is_demo && (
        <span className="absolute left-3 top-3 z-10 rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-700 backdrop-blur">
          Demo Data
        </span>
      )}
      <span className={`absolute right-3 top-3 z-10 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider capitalize ${STATUS_STYLES[vehicle.status] || STATUS_STYLES.available}`}>
        {vehicle.status}
      </span>
      <Link to={`/vehicle/${vehicle.id}`} className="block overflow-hidden">
        <div className="relative aspect-[16/10] overflow-hidden bg-[#DCE7FA]">
          <img
            src={imgUrl(img)}
            alt={`${vehicle.make} ${vehicle.model}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#FFFFFF] to-transparent" />
        </div>
      </Link>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gold">{vehicle.year} · {vehicle.body_type}</p>
            <Link to={`/vehicle/${vehicle.id}`}>
              <h3 className="font-display text-lg font-bold text-slate-900 hover:text-gold">{vehicle.make} {vehicle.model}</h3>
            </Link>
            <p className="text-sm text-slate-600">{vehicle.trim}</p>
          </div>
          {compareEnabled && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                data-testid={`vehicle-compare-checkbox-${vehicle.id}`}
                checked={compared}
                onCheckedChange={() => onCompare?.(vehicle)}
                className="border-white/20 data-[state=checked]:bg-gold data-[state=checked]:text-[#EEF3FC]"
              />
              Compare
            </label>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
          <span className="flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5 text-slate-500" /> {fmtNumber(vehicle.mileage)} mi</span>
          <span className="flex items-center gap-1.5"><Cog className="h-3.5 w-3.5 text-slate-500" /> {vehicle.transmission}</span>
          <span className="flex items-center gap-1.5"><Fuel className="h-3.5 w-3.5 text-slate-500" /> {vehicle.fuel_type}</span>
          <span className="flex items-center gap-1.5 truncate"><MapPin className="h-3.5 w-3.5 text-slate-500" /> {vehicle.location}</span>
        </div>
        <div className="mt-4 flex items-end justify-between border-t border-slate-200 pt-4">
          <div>
            <p className="font-display text-xl font-extrabold text-slate-900">{fmtMoney(vehicle.price)}</p>
            <p className="text-[11px] text-slate-500">Est. {fmtMoney(est)}/mo*</p>
          </div>
          <Link
            to={`/vehicle/${vehicle.id}`}
            data-testid={`vehicle-card-details-btn-${vehicle.id}`}
            className="rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-gold transition-colors hover:bg-gold hover:text-[#EEF3FC]"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};
